import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Download, FileText, Users, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  // Report 1 - Timesheet Report States
  const [report1FromDate, setReport1FromDate] = useState('');
  const [report1ToDate, setReport1ToDate] = useState('');
  const [report1WorkOrder, setReport1WorkOrder] = useState('');
  const [report1Data, setReport1Data] = useState([]);
  const [report1Loading, setReport1Loading] = useState(false);

  // Report 2 - Work Order Report States
  const [report2WorkOrder, setReport2WorkOrder] = useState('');
  const [report2Data, setReport2Data] = useState(null);
  const [report2Loading, setReport2Loading] = useState(false);

  // Report 3 - Daily Employee Report States
  const [report3Date, setReport3Date] = useState('');
  const [report3Employee, setReport3Employee] = useState('');
  const [report3Data, setReport3Data] = useState(null);
  const [report3Loading, setReport3Loading] = useState(false);

  // Common States
  const [workOrders, setWorkOrders] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchWorkOrders();
    fetchEmployees();
  }, []);

  const fetchWorkOrders = async () => {
    try {
      const response = await axios.get(`${API}/work-orders`);
      setWorkOrders(response.data);
    } catch (error) {
      console.error('Error fetching work orders:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Report 1 - Generate Timesheet Report
  const generateReport1 = async () => {
    if (!report1FromDate || !report1ToDate) {
      toast.error('Please select from date and to date');
      return;
    }

    setReport1Loading(true);
    try {
      const params = new URLSearchParams({
        date_from: report1FromDate,
        date_to: report1ToDate,
      });
      if (report1WorkOrder) {
        params.append('workorder_number', report1WorkOrder);
      }

      const response = await axios.get(`${API}/reports/timesheet-cost?${params.toString()}`);
      setReport1Data(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setReport1Loading(false);
    }
  };

  // Report 1 - Download Excel
  const downloadReport1 = () => {
    if (report1Data.length === 0) {
      toast.error('No data to download');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(report1Data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timesheet Report');
    XLSX.writeFile(wb, `Timesheet_Report_${report1FromDate}_to_${report1ToDate}.xlsx`);
    toast.success('Report downloaded!');
  };

  // Report 2 - Generate Work Order Report
  const generateReport2 = async () => {
    if (!report2WorkOrder) {
      toast.error('Please select a work order');
      return;
    }

    setReport2Loading(true);
    try {
      const response = await axios.get(`${API}/reports/work-order-detail?workorder_number=${report2WorkOrder}`);
      setReport2Data(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setReport2Loading(false);
    }
  };

  // Report 3 - Generate Daily Employee Report
  const generateReport3 = async () => {
    if (!report3Date || !report3Employee) {
      toast.error('Please select date and employee');
      return;
    }

    setReport3Loading(true);
    try {
      const response = await axios.get(`${API}/reports/daily-employee?date=${report3Date}&employee_name=${report3Employee}`);
      setReport3Data(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setReport3Loading(false);
    }
  };

  // Report 2 - Download Excel
  const downloadReport2 = () => {
    if (!report2Data) {
      toast.error('No data to download');
      return;
    }

    // Create employee breakdown sheet
    const employeeData = report2Data.cost_summary?.by_employee?.map(emp => ({
      'Employee Name': emp.employee_name,
      'Day Hours': emp.day_hours,
      'OT Hours': emp.ot_hours,
      'Total Cost (₹)': emp.total_cost
    })) || [];

    // Create machine breakdown sheet
    const machineData = report2Data.cost_summary?.by_machine?.map(m => ({
      'Machine Name': m.machine_name,
      'Employee Count': m.employee_count,
      'Total Hours': m.total_hours,
      'Total Cost (₹)': m.total_cost
    })) || [];

    // Create summary sheet
    const summaryData = [{
      'Work Order': report2Data.work_order_number,
      'Customer': report2Data.customer_name,
      'Item': report2Data.item_name,
      'Total Employees': report2Data.cost_summary?.total_employees || 0,
      'Total Hours': report2Data.cost_summary?.total_hours || 0,
      'Day Hours': report2Data.cost_summary?.total_day_hours || 0,
      'OT Hours': report2Data.cost_summary?.total_ot_hours || 0,
      'Total Cost (₹)': report2Data.cost_summary?.total_cost || 0
    }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Summary');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(employeeData), 'By Employee');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(machineData), 'By Machine');
    XLSX.writeFile(wb, `WorkOrder_${report2WorkOrder}_Cost_Report.xlsx`);
    toast.success('Report downloaded!');
  };

  // Report 3 - Download Excel
  const downloadReport3 = () => {
    if (!report3Data) {
      toast.error('No data to download');
      return;
    }

    // Create summary data
    const summaryData = [{
      'Date': report3Date,
      'Employee': report3Data.employee_name,
      'Total Hours': report3Data.total_hours,
      'Day Shift Hours': report3Data.day_shift_hours,
      'Overtime Hours': report3Data.overtime_hours,
      'Total Cost (₹)': report3Data.total_cost
    }];

    // Create machine breakdown
    const machineData = report3Data.machine_breakdown?.map(m => ({
      'Machine Name': m.machine_name,
      'Work Order': m.workorder_number,
      'Start Time': m.start_time,
      'End Time': m.end_time,
      'Hours': m.hours,
      'Shift Type': m.shift_type,
      'Status': m.work_order_status
    })) || [];

    // Create timeline data
    const timelineData = report3Data.timeline?.map(t => ({
      'Time': t.time,
      'Event': t.event,
      'Machine': t.machine,
      'Work Order': t.workorder
    })) || [];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Summary');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(machineData), 'Machine Breakdown');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(timelineData), 'Timeline');
    XLSX.writeFile(wb, `Daily_Employee_Report_${report3Date}_${report3Employee}.xlsx`);
    toast.success('Report downloaded!');
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8" style={{ background: 'radial-gradient(ellipse at center, #0A1910 0%, #000000 70%)' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-200 mb-2">Reports & Analytics</h1>
          <p className="text-lg text-slate-400">Generate comprehensive reports and analyze your data</p>
        </div>

        {/* Three Report Cards */}
        <div className="grid grid-cols-1 gap-6">
          
          {/* REPORT 1 - Timesheet Report with Labor Cost */}
          <Card className="bg-black/60 border border-slate-700">
            <CardHeader className="bg-slate-800/80">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-slate-200" />
                <CardTitle className="text-2xl font-bold text-slate-200">Report 1: Timesheet with Labor Cost</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label className="text-slate-300 font-semibold flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Select From Date *
                  </Label>
                  <div className="relative mt-2">
                    <Input
                      type="date"
                      value={report1FromDate}
                      onChange={(e) => setReport1FromDate(e.target.value)}
                      className="bg-gray-900 border-slate-600 text-white pr-10 [color-scheme:dark]"
                    />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 font-semibold flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Select To Date *
                  </Label>
                  <div className="relative mt-2">
                    <Input
                      type="date"
                      value={report1ToDate}
                      onChange={(e) => setReport1ToDate(e.target.value)}
                      className="bg-gray-900 border-slate-600 text-white pr-10 [color-scheme:dark]"
                    />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 font-semibold">Work Order (Optional)</Label>
                  <Select value={report1WorkOrder} onValueChange={(val) => setReport1WorkOrder(val === "all" ? "" : val)}>
                    <SelectTrigger className="mt-2 bg-gray-900 border-slate-600 text-white">
                      <SelectValue placeholder="All Work Orders" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Work Orders</SelectItem>
                      {workOrders.filter(wo => wo?.header?.work_order_number).map((wo) => (
                        <SelectItem key={wo.id} value={wo.header.work_order_number}>
                          {wo.header.work_order_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={generateReport1}
                  disabled={report1Loading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {report1Loading ? 'Generating...' : 'Generate Report'}
                </Button>
                {report1Data.length > 0 && (
                  <Button
                    onClick={downloadReport1}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Download className="mr-2 h-4 w-4" /> Download Excel
                  </Button>
                )}
              </div>

              {/* Report 1 Data Table */}
              {report1Data.length > 0 && (
                <div className="mt-6 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-700/30">
                        <TableHead className="text-slate-300">Date</TableHead>
                        <TableHead className="text-slate-300">Employee</TableHead>
                        <TableHead className="text-slate-300">Work Order</TableHead>
                        <TableHead className="text-slate-300">Machine</TableHead>
                        <TableHead className="text-slate-300">Start Time</TableHead>
                        <TableHead className="text-slate-300">End Time</TableHead>
                        <TableHead className="text-slate-300">Duration (Hrs)</TableHead>
                        <TableHead className="text-slate-300">Shift Type</TableHead>
                        <TableHead className="text-slate-300">Day Hrs</TableHead>
                        <TableHead className="text-slate-300">OT Hrs</TableHead>
                        <TableHead className="text-slate-300">Salary</TableHead>
                        <TableHead className="text-slate-300">Day Cost</TableHead>
                        <TableHead className="text-slate-300">OT Cost</TableHead>
                        <TableHead className="text-slate-300">Total Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report1Data.map((row, idx) => (
                        <TableRow key={idx} className="text-gray-300">
                          <TableCell>{row.date}</TableCell>
                          <TableCell>{row.employee_name}</TableCell>
                          <TableCell>{row.workorder_number}</TableCell>
                          <TableCell>{row.machine_name}</TableCell>
                          <TableCell>{row.start_time}</TableCell>
                          <TableCell>{row.end_time}</TableCell>
                          <TableCell>{row.total_hours}</TableCell>
                          <TableCell>{row.shift_type}</TableCell>
                          <TableCell>{row.day_hours}</TableCell>
                          <TableCell>{row.ot_hours}</TableCell>
                          <TableCell>₹{row.employee_salary}</TableCell>
                          <TableCell>₹{row.day_cost}</TableCell>
                          <TableCell>₹{row.ot_cost}</TableCell>
                          <TableCell className="font-bold">₹{row.total_cost}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* REPORT 2 - Work Order Report */}
          <Card className="bg-black/60 border border-slate-700">
            <CardHeader className="bg-slate-800/80">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-slate-200" />
                <CardTitle className="text-2xl font-bold text-slate-200">Report 2: Work Order Details & Cost</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-slate-300 font-semibold">Select Work Order *</Label>
                  <Select value={report2WorkOrder} onValueChange={setReport2WorkOrder}>
                    <SelectTrigger className="mt-2 bg-gray-900 border-slate-600 text-white">
                      <SelectValue placeholder="Select Work Order" />
                    </SelectTrigger>
                    <SelectContent>
                      {workOrders.filter(wo => wo?.header?.work_order_number).map((wo) => (
                        <SelectItem key={wo.id} value={wo.header.work_order_number}>
                          {wo.header.work_order_number} - {wo.header.customer_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={generateReport2}
                  disabled={report2Loading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {report2Loading ? 'Generating...' : 'Generate Report'}
                </Button>
                {report2Data && (
                  <Button
                    onClick={downloadReport2}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download Excel
                  </Button>
                )}
              </div>

              {/* Report 2 Data */}
              {report2Data && (
                <Tabs defaultValue="details" className="mt-6">
                  <TabsList className="bg-slate-700/50">
                    <TabsTrigger value="details">Work Order Details</TabsTrigger>
                    <TabsTrigger value="cost">Cost Summary</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="details" className="text-gray-300">
                    <div className="space-y-4">
                      <h3 className="text-xl font-bold text-slate-300">Work Order Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div><span className="text-slate-300">Work Order:</span> {report2Data.work_order_number}</div>
                        <div><span className="text-slate-300">Customer:</span> {report2Data.customer_name}</div>
                        <div><span className="text-slate-300">Item:</span> {report2Data.item_name}</div>
                        <div><span className="text-slate-300">Quantity:</span> {report2Data.required_quantity}</div>
                        <div><span className="text-slate-300">Delivery Date:</span> {report2Data.delivery_date}</div>
                        <div><span className="text-slate-300">Status:</span> {report2Data.status}</div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="cost">
                    <div className="space-y-6">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <Card className="bg-slate-700/30 border-slate-600">
                          <CardContent className="pt-6">
                            <div className="text-3xl font-bold text-slate-300">{report2Data.cost_summary?.total_employees || 0}</div>
                            <div className="text-gray-400">Total Employees</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-slate-700/30 border-slate-600">
                          <CardContent className="pt-6">
                            <div className="text-3xl font-bold text-slate-300">{report2Data.cost_summary?.total_hours?.toFixed(2) || 0}</div>
                            <div className="text-gray-400">Total Hours</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-slate-700/30 border-slate-600">
                          <CardContent className="pt-6">
                            <div className="text-3xl font-bold text-slate-300">₹{report2Data.cost_summary?.total_cost?.toFixed(2) || 0}</div>
                            <div className="text-gray-400">Total Labor Cost</div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Employee-wise breakdown */}
                      <div>
                        <h4 className="text-lg font-bold text-slate-300 mb-3">Cost by Employee</h4>
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-700/30">
                              <TableHead className="text-slate-300">Employee</TableHead>
                              <TableHead className="text-slate-300">Day Hours</TableHead>
                              <TableHead className="text-slate-300">OT Hours</TableHead>
                              <TableHead className="text-slate-300">Total Cost</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {report2Data.cost_summary?.by_employee?.map((emp, idx) => (
                              <TableRow key={idx} className="text-gray-300">
                                <TableCell>{emp.employee_name}</TableCell>
                                <TableCell>{emp.day_hours}</TableCell>
                                <TableCell>{emp.ot_hours}</TableCell>
                                <TableCell className="font-bold">₹{emp.total_cost}</TableCell>
                              </TableRow>
                            )) || []}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Machine-wise breakdown */}
                      <div>
                        <h4 className="text-lg font-bold text-slate-300 mb-3">Cost by Machine</h4>
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-700/30">
                              <TableHead className="text-slate-300">Machine</TableHead>
                              <TableHead className="text-slate-300">Employees</TableHead>
                              <TableHead className="text-slate-300">Total Hours</TableHead>
                              <TableHead className="text-slate-300">Total Cost</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {report2Data.cost_summary?.by_machine?.map((machine, idx) => (
                              <TableRow key={idx} className="text-gray-300">
                                <TableCell>{machine.machine_name}</TableCell>
                                <TableCell>{machine.employee_count}</TableCell>
                                <TableCell>{machine.total_hours}</TableCell>
                                <TableCell className="font-bold">₹{machine.total_cost}</TableCell>
                              </TableRow>
                            )) || []}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>

          {/* REPORT 3 - Daily Employee Report */}
          <Card className="bg-black/60 border border-slate-700">
            <CardHeader className="bg-slate-800/80">
              <div className="flex items-center gap-3">
                <Users className="h-6 w-6 text-slate-200" />
                <CardTitle className="text-2xl font-bold text-slate-200">Report 3: Daily Employee Work Report</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-slate-300 font-semibold flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Select Date *
                  </Label>
                  <div className="relative mt-2">
                    <Input
                      type="date"
                      value={report3Date}
                      onChange={(e) => setReport3Date(e.target.value)}
                      className="bg-gray-900 border-slate-600 text-white pr-10 [color-scheme:dark]"
                    />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 font-semibold">Select Employee *</Label>
                  <Select value={report3Employee} onValueChange={setReport3Employee}>
                    <SelectTrigger className="mt-2 bg-gray-900 border-slate-600 text-white">
                      <SelectValue placeholder="Select Employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.name}>
                          {emp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={generateReport3}
                  disabled={report3Loading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {report3Loading ? 'Generating...' : 'Generate Report'}
                </Button>
                {report3Data && (
                  <Button
                    onClick={downloadReport3}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download Excel
                  </Button>
                )}
              </div>

              {/* Report 3 Data */}
              {report3Data && (
                <div className="mt-6 space-y-6">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <Card className="bg-slate-700/30 border-slate-600">
                      <CardContent className="pt-6">
                        <div className="text-3xl font-bold text-slate-300">{report3Data.total_hours?.toFixed(2) || 0}</div>
                        <div className="text-gray-400">Total Hours</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-700/30 border-slate-600">
                      <CardContent className="pt-6">
                        <div className="text-3xl font-bold text-slate-300">{report3Data.day_shift_hours?.toFixed(2) || 0}</div>
                        <div className="text-gray-400">Day Hours</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-700/30 border-slate-600">
                      <CardContent className="pt-6">
                        <div className="text-3xl font-bold text-slate-300">{report3Data.overtime_hours?.toFixed(2) || 0}</div>
                        <div className="text-gray-400">Overtime Hours</div>
                      </CardContent>
                    </Card>
                  </div>

                  <div>
                    <h4 className="text-lg font-bold text-slate-300 mb-3">Machines Worked</h4>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-700/30">
                          <TableHead className="text-slate-300">Machine</TableHead>
                          <TableHead className="text-slate-300">Total Hours</TableHead>
                          <TableHead className="text-slate-300">Day Hours</TableHead>
                          <TableHead className="text-slate-300">OT Hours</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {report3Data.machine_breakdown?.map((machine, idx) => (
                          <TableRow key={idx} className="text-gray-300">
                            <TableCell>{machine.machine_name}</TableCell>
                            <TableCell>{machine.hours?.toFixed(2)}</TableCell>
                            <TableCell>{machine.day_shift_hours?.toFixed(2)}</TableCell>
                            <TableCell>{machine.overtime_hours?.toFixed(2)}</TableCell>
                          </TableRow>
                        )) || []}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Standard Break Times */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="bg-orange-500/10 border-orange-500/30">
                      <CardContent className="pt-6">
                        <div className="text-xl font-bold text-orange-400">
                          {report3Data.lunch_break || "N/A"}
                        </div>
                        <div className="text-gray-400">Lunch Break</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-500/10 border-purple-500/30">
                      <CardContent className="pt-6">
                        <div className="text-xl font-bold text-purple-400">
                          {report3Data.evening_break || "N/A"}
                        </div>
                        <div className="text-gray-400">Evening Break (Overtime)</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Shift Info */}
                  <div className="p-4 bg-gray-800/50 rounded-lg border border-slate-600">
                    <div className="text-sm text-gray-400 mb-2">Shift Timings:</div>
                    <div className="flex flex-wrap gap-4">
                      <div>
                        <span className="text-green-400 font-bold">Day Shift:</span>
                        <span className="text-gray-300 ml-2">{report3Data.shift_info?.day_shift || "09:00 - 13:00, 13:30 - 17:30"}</span>
                      </div>
                      <div>
                        <span className="text-orange-400 font-bold">Lunch:</span>
                        <span className="text-gray-300 ml-2">{report3Data.shift_info?.lunch_break || "13:00 - 13:30 (30 mins)"}</span>
                      </div>
                      <div>
                        <span className="text-slate-300 font-bold">Overtime:</span>
                        <span className="text-gray-300 ml-2">{report3Data.shift_info?.overtime || "Before 09:00 & After 17:30"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div>
                    <h4 className="text-lg font-bold text-slate-300 mb-3">Work Timeline</h4>
                    <div className="space-y-2">
                      {/* Show standard breaks first */}
                      {report3Data.breaks?.map((breakItem, idx) => (
                        <div key={`break-${idx}`} className="flex items-center gap-4 p-3 bg-orange-900/30 rounded border border-orange-500/30">
                          <div className={`px-3 py-1 rounded text-xs font-bold ${breakItem.type === 'lunch' ? 'bg-orange-500 text-white' : 'bg-purple-500 text-white'}`}>
                            {breakItem.type === 'lunch' ? 'LUNCH' : 'BREAK'}
                          </div>
                          <div className="text-gray-300">
                            <span className="font-bold">{breakItem.start_time}</span> - <span className="font-bold">{breakItem.end_time}</span>
                          </div>
                          <div className="text-orange-400">{breakItem.description}</div>
                          <div className="text-gray-400 text-sm">{breakItem.duration_hours?.toFixed(2)} hrs</div>
                        </div>
                      )) || []}
                      
                      {/* Work sessions */}
                      {report3Data.timeline?.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-3 bg-gray-900/50 rounded border border-slate-600">
                          <div className="px-3 py-1 rounded text-xs font-bold bg-green-500 text-white">
                            WORK
                          </div>
                          <div className="text-gray-300">
                            <span className="font-bold">{item.start_time}</span> - <span className="font-bold">{item.end_time}</span>
                          </div>
                          {item.machine_name && (
                            <div className="text-slate-300">{item.machine_name}</div>
                          )}
                          <div className="text-gray-400 text-sm">{item.duration_hours?.toFixed(2)} hrs</div>
                        </div>
                      )) || []}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Reports;